import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { AppInputField, AppSelectField } from '@/form/FormUI';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import logger from '@/util/logger';
import { zodResolver } from '@hookform/resolvers/zod';
import { ConsiderationDao, createId } from '@rock/db-admin';
import {
    type Consideration,
    type ConsiderationId,
    type ConsiderationTypeId,
    EquipmentIdSchema,
    type HomeId,
    SpaceIdSchema,
} from '@rock/types';
import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { type FieldErrors, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as z from 'zod';

const log = logger('AddTestConsiderationButton');
const considerationDao = new ConsiderationDao();

const ConsiderationFormSchema = z
    .object({
        title: z.string().min(1, 'Title is required').max(120, 'Keep the title under 120 characters'),
        scope: z.enum(['home', 'space', 'equipment']),
        spaceId: z.string().optional(),
        equipmentId: z.string().optional(),
    })
    .superRefine((values, ctx) => {
        if (values.scope !== 'home' && !values.spaceId) {
            ctx.addIssue({
                code: 'custom',
                message: 'Space ID is required for this scope',
                path: ['spaceId'],
            });
        }
        if (values.scope === 'equipment' && !values.equipmentId) {
            ctx.addIssue({
                code: 'custom',
                message: 'Equipment ID is required when scope is equipment',
                path: ['equipmentId'],
            });
        }
    });

export type ConsiderationFormValues = z.infer<typeof ConsiderationFormSchema>;

type AddTestConsiderationButtonProps = {
    homeId: HomeId;
};

export const AddTestConsiderationButton: FC<AddTestConsiderationButtonProps> = ({ homeId }) => {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const form = useForm({
        resolver: zodResolver(ConsiderationFormSchema),
        defaultValues: {
            title: '',
            scope: 'home',
            spaceId: '',
            equipmentId: '',
        },
    });

    const scope = form.watch('scope');

    const closeDialog = useCallback(() => {
        form.reset();
        setOpen(false);
    }, [form]);

    const onInvalid = useCallback((errors: FieldErrors) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    const onSubmit = useCallback(
        async (values: ConsiderationFormValues) => {
            setSubmitting(true);
            try {
                const considerationId = createId<ConsiderationId>();
                const now = new Date().toISOString();
                const payload: Consideration = {
                    object: 'consideration',
                    schema: 1,
                    id: considerationId,
                    homeId,
                    status: 'pending',
                    sourceType: 'manual',
                    title: values.title,
                    createdAt: now,
                    updatedAt: now,
                    spaceId: values.scope === 'home' ? undefined : SpaceIdSchema.parse(values.spaceId),
                    coveredEquipmentIds:
                        values.scope === 'equipment' ? [EquipmentIdSchema.parse(values.equipmentId)] : undefined,
                    typeId: 'todo' as ConsiderationTypeId, //TODO
                };

                await considerationDao.setConsideration(homeId, considerationId, payload);

                toast.success('Consideration added', {
                    description: 'A test consideration has been created for this home.',
                });
                log.info('Created test consideration', { homeId, considerationId, scope: values.scope });
                closeDialog();
            } catch (err) {
                log.error('Failed to create test consideration', { err, homeId, scope: values.scope });
                toast.error('Unable to create consideration', {
                    description: err instanceof Error ? err.message : 'Unknown error',
                });
            } finally {
                setSubmitting(false);
            }
        },
        [closeDialog, homeId],
    );

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !submitting && setOpen(isOpen)}>
            <DialogTrigger asChild>
                <Button className="cursor-pointer">Add Test Consideration</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                    <DialogTitle>Add Test Consideration</DialogTitle>
                    <DialogDescription>Quickly add a consideration to this home for testing.</DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form className="space-y-3" onSubmit={form.handleSubmit(onSubmit, onInvalid)}>
                        <AppInputField
                            control={form.control}
                            name="title"
                            label="Title"
                            placeholder="Replace air filter"
                        />
                        <AppSelectField
                            control={form.control}
                            name="scope"
                            label="Scope"
                            placeholder="Select scope"
                            options={[
                                { label: 'Home', value: 'home' },
                                { label: 'Space', value: 'space' },
                                { label: 'Equipment', value: 'equipment' },
                            ]}
                        />
                        {scope !== 'home' && (
                            <AppInputField
                                control={form.control}
                                name="spaceId"
                                label="Space ID"
                                placeholder="living-room"
                            />
                        )}
                        {scope === 'equipment' && (
                            <AppInputField
                                control={form.control}
                                name="equipmentId"
                                label="Equipment ID"
                                placeholder="furnace-123"
                            />
                        )}
                        <DialogFooter>
                            <Button type="submit" className="cursor-pointer" disabled={submitting}>
                                {submitting ? 'Adding…' : 'Add Consideration'}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};
